import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import axios from "../utils/axios";
import { IDeleteApiResponse, IError } from "../utils/api.types";

import {
    IAccountResponse,
    IAccounts,
    IAccountUpdatePayload,
    IAccountBasicFormInputs,
    InvoiceAccountResponse
} from "../utils/account.types";

/**
 * Fetch Accounts async thunk.
*/
export const fetchAccounts = createAsyncThunk(
    'account/fetchAccounts',
    async (): Promise<IAccounts> => {
        const response = await axios.get('/api/v1/account');

        return response.data;
    }
);

export const selectedAccountID = createAction("account/selectedAccountID", (id) => {
    return {
        type: 'account/selectedAccountID',
        payload: id
    }
})

/**
 * Fetch Accounts async thunk.
*/
export const fetchAccountById = createAsyncThunk(
    'account/fetchAccountById',
    async (id: string): Promise<IAccounts> => {
        const response = await axios.get(`/api/v1/account/${id}`);
        return response.data.accounts[0];
    }
);

/**
 * Fetch counterPartyAccounts async thunk.
*/
export const fetchCounterPartyAccountById = createAsyncThunk(
    'account/fetchCounterPartyAccountById',
    async (id: string): Promise<IAccounts> => {
        const response = await axios.get(`/api/v1/account/counter-party/${id}`);
        return response.data.accounts[0];
    }
);

/**
 * Updtae legal data async Thunk.
 */
export const updateLegalData = createAsyncThunk(
    'account/updateLegalData',
    async (data: any, thunkApi) => {
        const { id, legalPayload } = data;
        try {
            const response = await axios.put(`/api/v1/account/${id}/legal`, legalPayload);
            const { data } = response;
            if (response.status === 200) {
                return data;
            } else {
                return thunkApi.rejectWithValue(data.error);
            }
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response.data);
        }
    }
);

/**
 * Updtae legal data async Thunk.
 */
 export const updateAccountingData = createAsyncThunk(
    'account/updateAccountingData',
    async (data: any, thunkApi) => {
        const { id, accountingPayload } = data;
        try {
            const response = await axios.put(`/api/v1/account/${id}/accounting`, accountingPayload);
            const { data } = response;
            if (response.status === 200) {
                return data;
            } else {
                return thunkApi.rejectWithValue(data.error);
            }
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response.data);
        }
    }
);

/**
 * Add Account async Thunk.
 */
export const addAccount = createAsyncThunk(
    'account/addAccount',
    async (accountPayload: IAccountBasicFormInputs): Promise<IAccountResponse> => {
        const response = await axios.post('/api/v1/account', accountPayload);

        return response.data
    }
);

/**
 * Update Account async Thunk.
 */
export const updateAccount = createAsyncThunk<
    IAccountResponse,
    IAccountUpdatePayload,
    {
        rejectValue: IError,
    }
>(
    'account/updateAccount',
    async ({ id, accountPayload }: IAccountUpdatePayload, thunkApi) => {
        try {
            const response = await axios.put(`/api/v1/account/${id}`, accountPayload);

            const { data } = response;

            if (response.status === 200) {
                return data;
            } else {
                return thunkApi.rejectWithValue(data.error);
            }
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response);
        }
    }
);


/**
 * Update Account async Thunk.
 */
export const removeAccountLogoOrSign = createAsyncThunk<
    IAccountResponse,
    IAccountUpdatePayload,
    {
        rejectValue: IError,
    }
>(
    'account/removeAccountLogoOrSign',
    async ({ id, accountPayload }: IAccountUpdatePayload, thunkApi) => {
        try {
            console.log(accountPayload);
            const response = await axios.put(`/api/v1/account/${id}/removeLogoOrSign`, accountPayload);

            const { data } = response;

            if (response.status === 200) {
                return data;
            } else {
                return thunkApi.rejectWithValue(data.error);
            }
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response);
        }
    }
);

/**
 * Delete Account async Thunk.
 */
export const deleteAccount = createAsyncThunk(
    'account/deleteAccount',
    async (id: string): Promise<IDeleteApiResponse> => {
        const response = await axios.delete(`/api/v1/account/${id}`);

        response.data['id'] = id;

        return response.data
    }
);

// Fetch accounts by name async Thunk.
export const fetchAccountsByName  = createAsyncThunk(
    'account/search',
    async (name: string): Promise<InvoiceAccountResponse> => {
        const response = await axios.get(`/api/v1/account/search?name=${name}`);
        return response.data;
    }
);

export const fetchBuyerOrSupplierByName  = createAsyncThunk(
    'account/searchBuyerOrSupplier',
    async (queryString: string): Promise<InvoiceAccountResponse> => {
        const response = await axios.get(`/api/v1/account/search?${queryString}`);
        return response.data;
    }
);

// Unofy CRM: search leads/patients for the current tenant (backend proxies the CRM).
// queryString e.g. `q=rao&type=lead&page=1&limit=20` (type optional).
export const searchUnofyCrm = createAsyncThunk(
    'account/searchUnofyCrm',
    async (queryString: string): Promise<any> => {
        const response = await axios.get(`/api/v1/crm-import/search?${queryString}`);
        return response.data;
    }
);

// Unofy CRM: fetch a single lead/patient mapped to the buyer-form shape (prefill).
export const getUnofyCrmDetail = createAsyncThunk(
    'account/getUnofyCrmDetail',
    async (leadId: string): Promise<any> => {
        const response = await axios.get(`/api/v1/crm-import/detail/${encodeURIComponent(leadId)}`);
        return response.data;
    }
);

// Unofy CRM: the CRM's own users, for the invoice Lead form's agent picker.
export const fetchUnofyCrmAgents = createAsyncThunk(
    'account/fetchUnofyCrmAgents',
    async (): Promise<any> => {
        const response = await axios.get('/api/v1/crm-import/agents');
        return response.data;
    }
);

// Lead sources from our own CRM masters. `scope=account` drops the shared system defaults.
export const fetchLeadSources = createAsyncThunk(
    'account/fetchLeadSources',
    async (): Promise<any> => {
        const response = await axios.get('/api/v1/crm/lead-sources?scope=account');
        return response.data;
    }
);

// Lead sub-sources under one source, same account-only scoping.
export const fetchLeadSubSources = createAsyncThunk(
    'account/fetchLeadSubSources',
    async (): Promise<any> => {
        const response = await axios.get('/api/v1/crm/lead-sub-sources?scope=account');
        return response.data;
    }
);

//Add Account async Thunk.
 export const addBuyerSellerAccount = createAsyncThunk(
    'account/addBuyerSellerAccount',
    async (accountPayload: IAccountBasicFormInputs, { rejectWithValue }): Promise<any> => {
        try {
            const response = await axios.post('/api/v1/account/buyer-seller', accountPayload);
            return response.data
        } catch (err: any) {
            // Surface the backend's JSON error (e.g. CRM duplicate-phone 409) to the caller.
            return rejectWithValue(err?.response?.data || { message: 'Failed to create account' });
        }
    }
);

// Update CounterParty/Customer async Thunk.
export const updateBuyerSellerAccount = createAsyncThunk(
    'account/updateBuyerSellerAccount',
    async (data: any, thunkApi): Promise<IAccountResponse> => {
        console.log(data);
        const { customerId, customerPayload } = data;

        const response = await axios.post(`/api/v1/customers/one/${customerId}`, customerPayload);
        return response.data
    }
);

// WhatsApp notification settings: fetch the per-account toggles (backend creates defaults if none).
export const fetchAccountSettings = createAsyncThunk(
    'account/fetchAccountSettings',
    async (accountId: string): Promise<any> => {
        const response = await axios.get(`/api/v1/account/${accountId}/settings`);
        return response.data;
    }
);

// Notification settings: update WhatsApp and/or email toggles. Either group may be passed.
export const updateAccountSettings = createAsyncThunk(
    'account/updateAccountSettings',
    async ({ accountId, whatsappNotifications, emailNotifications, paymentDueReminderDays, newCustomerWindowHours, stockLowThresholds, businessSummaryRecipients, gstr1ReportRecipients, reportShareRecipients }: { accountId: string; whatsappNotifications?: any; emailNotifications?: any; paymentDueReminderDays?: number; newCustomerWindowHours?: number; stockLowThresholds?: { quantity: number; unit: string }[]; businessSummaryRecipients?: { emails?: string[]; phoneNumbers?: string[] }; gstr1ReportRecipients?: { emails?: string[] }; reportShareRecipients?: { master?: { emails?: string[] }; perReport?: Record<string, { emails?: string[] }> } }): Promise<any> => {
        const body: any = {};
        if (whatsappNotifications) body.whatsappNotifications = whatsappNotifications;
        if (emailNotifications) body.emailNotifications = emailNotifications;
        if (paymentDueReminderDays !== undefined) body.paymentDueReminderDays = paymentDueReminderDays;
        if (newCustomerWindowHours !== undefined) body.newCustomerWindowHours = newCustomerWindowHours;
        if (stockLowThresholds) body.stockLowThresholds = stockLowThresholds;
        if (businessSummaryRecipients) body.businessSummaryRecipients = businessSummaryRecipients;
        if (gstr1ReportRecipients) body.gstr1ReportRecipients = gstr1ReportRecipients;
        if (reportShareRecipients) body.reportShareRecipients = reportShareRecipients;
        if (newCustomerWindowHours !== undefined) body.newCustomerWindowHours = newCustomerWindowHours;
        const response = await axios.put(`/api/v1/account/${accountId}/settings`, body);
        return response.data;
    }
);

// Business summary: fetch the formatted figures for a date range (preview only, sends nothing).
// The backend derives the period label from the dates, so we only send startDate/endDate.
export const fetchBusinessSummaryPreview = createAsyncThunk(
    'account/fetchBusinessSummaryPreview',
    async ({ accountId, startDate, endDate, periodName }: { accountId: string; startDate: string; endDate: string; periodName?: string }): Promise<any> => {
        let params = `startDate=${startDate}&endDate=${endDate}`;
        if (periodName) params += `&periodName=${encodeURIComponent(periodName)}`;
        const response = await axios.get(`/api/v1/account/${accountId}/business-summary?${params}`);
        return response.data;
    }
);

// Business summary: send the figures for a date range to the merchant's own WhatsApp number.
export const sendBusinessSummaryWhatsApp = createAsyncThunk(
    'account/sendBusinessSummaryWhatsApp',
    async ({ accountId, startDate, endDate, periodName }: { accountId: string; startDate: string; endDate: string; periodName?: string }): Promise<any> => {
        const response = await axios.post(`/api/v1/account/${accountId}/business-summary/send`, { startDate, endDate, periodName });
        return response.data;
    }
);
